import { animate, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { RouterLink, RouterOutlet } from '@angular/router';
import { FuseDrawerComponent } from '@ista-ui/angular';
import { icBrand } from '@shared/branding';
import { AcceptCookiesComponent } from '@shared/disclaimer';
import { AppFooterComponent } from '@shared/layout/components/app-footer';
import { BreadcrumbComponent } from '@shared/layout/components/breadcrumb/breadcrumb.component';
import { MainNavigationComponent } from '@shared/layout/components/main-navigation/main-navigation.component';
import { SpinnerComponent } from '@shared/layout/components/spinner';
import { TopNavigationComponent } from '@shared/layout/components/top-navigation';
import { FallbackImageSourceDirective } from '@shared/layout/directives/fallback-image-source';
import { UiBreakpoints } from '@shared/layout/enums';
import { LanguageSelectionService } from '@shared/localization';
import { InlineNotificationComponent, ToastsComponent } from '@shared/notifications';
import type { Observable } from 'rxjs';
import { delay, map, Subscription, tap } from 'rxjs';
import { BreadcrumbService } from '../breadcrumb';
import { DefaultLayoutService } from './default-layout.service';
import type { BreadcrumbConfiguration } from '@shared/layout/models';

@Component({
  selector: 'ic-default-layout',
  templateUrl: './default-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('0.6s cubic-bezier(.5,0,0,1)', style({ opacity: 1 }))]),
      transition(':leave', [animate('0.2s cubic-bezier(.5,0,0,1)', style({ opacity: 0 }))]),
    ]),
  ],
  imports: [
    FuseDrawerComponent,
    RouterLink,
    RouterOutlet,
    FallbackImageSourceDirective,
    MainNavigationComponent,
    MatToolbar,
    MatIconButton,
    MatIcon,
    TopNavigationComponent,
    BreadcrumbComponent,
    InlineNotificationComponent,
    AppFooterComponent,
    AcceptCookiesComponent,
    SpinnerComponent,
    ToastsComponent,
    AsyncPipe,
  ],
})
export class DefaultLayoutComponent implements OnDestroy {
  // injectables
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly languageSelection = inject(LanguageSelectionService);
  private readonly defaultLayoutService = inject(DefaultLayoutService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  public readonly brandName = inject(icBrand);
  // inputs
  public readonly breadcrumb = input<BreadcrumbConfiguration>();
  // host bindings
  @HostBinding('class')
  public cssClass = 'ista-ui-layout';
  // state
  public breadcrumb$ = this.breadcrumbService.breadcrumbs$;

  public readonly expanded = signal<boolean>(true);
  public readonly isBranded = signal(!!this.brandName);
  public readonly brandLogo = computed(() =>
    this.isBranded() ? `/s2/brands/${this.brandName}/logo_1.png` : '/assets/img/ic_logo_ocean_blue.svg',
  );
  public readonly assetsRev = +new Date();

  public isSmallScreen$: Observable<boolean>;
  public locale$ = this.languageSelection.locale$;
  private subscriptions: Subscription = new Subscription();

  public constructor() {
    this.subscribeToBreakpointChanges();
    this.subscribeToExpandedChanges();
    effect(() => {
      this.breadcrumbService.change(this.breadcrumb());
    });
  }

  public toggleDrawer() {
    this.expanded.update((expanded) => !expanded);
    this.defaultLayoutService.setUserState(this.expanded());
  }

  public onDrawerToggled(open: boolean) {
    if (this.expanded() !== open) {
      this.expanded.set(open);
    }
  }

  private subscribeToBreakpointChanges() {
    this.isSmallScreen$ = this.breakpointObserver.observe([UiBreakpoints.XS, UiBreakpoints.SM, UiBreakpoints.MD]).pipe(
      map(({ matches }) => matches),
      delay(100),
      tap((isSmallScreen) => {
        if (!isSmallScreen) {
          return;
        }

        this.expanded.set(false);
      }),
    );
  }

  private subscribeToExpandedChanges() {
    this.subscriptions.add(
      this.defaultLayoutService.expanded$.subscribe((value: boolean) => {
        this.expanded.set(value);
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
